import { Package, Truck, BarChart3, Cog, Factory } from 'lucide-react';
import { Product } from './types';

export const operationsProducts: Product[] = [
  {
    id: 'production-optimization-suite',
    title: 'Production Optimization Suite',
    subtitle: 'Manufacturing excellence platform',
    description: 'Comprehensive production optimization with real-time monitoring, quality control, efficiency analysis, and predictive maintenance.',
    icon: Factory,
    image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Real-time production monitoring',
      'Quality control systems',
      'Efficiency optimization',
      'Predictive maintenance',
      'Resource planning',
      'Performance analytics',
      'Workflow automation',
      'Cost optimization'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#manufacturing', '#production', '#optimization', '#quality', '#automation']
  },
  {
    id: 'warehouse-management-system',
    title: 'Warehouse Management System',
    subtitle: 'Optimised inventory control',
    description: 'Advanced warehouse management with real-time tracking, automated storage, and efficient order fulfillment.',
    icon: Package,
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Real-time inventory tracking',
      'Automated storage',
      'Order fulfillment',
      'Shipping management',
      'Cross-docking',
      'Yard management',
      'Labor management',
      'Reporting & analytics'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#warehouse', '#inventory', '#logistics', '#supply-chain', '#automation']
  },
  {
    id: 'transportation-management-system',
    title: 'Transportation Management System',
    subtitle: 'Efficient logistics solution',
    description: 'Comprehensive transportation management with route optimization, real-time tracking, and freight cost management.',
    icon: Truck,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Route optimization',
      'Real-time tracking',
      'Freight cost management',
      'Carrier management',
      'Load planning',
      'Delivery confirmation',
      'Reporting & analytics',
      'Mobile app'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#transportation', '#logistics', '#freight', '#shipping', '#optimization']
  },
  {
    id: 'supply-chain-analytics',
    title: 'Supply Chain Analytics Platform',
    subtitle: 'Data-driven supply chain insights',
    description: 'Advanced analytics platform for supply chain optimization with predictive modeling, demand forecasting, and performance monitoring.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Predictive modeling',
      'Demand forecasting',
      'Performance monitoring',
      'Risk management',
      'Inventory optimization',
      'Supplier performance',
      'Reporting & dashboards',
      'Scenario planning'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#supply-chain', '#analytics', '#data-driven', '#forecasting', '#optimization']
  },
  {
    id: 'asset-maintenance-management',
    title: 'Asset & Maintenance Management',
    subtitle: 'Equipment lifecycle management',
    description: 'Comprehensive asset and maintenance management with preventive maintenance, equipment tracking, and repair management.',
    icon: Cog,
    image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Preventive maintenance',
      'Equipment tracking',
      'Repair management',
      'Warranty tracking',
      'Condition monitoring',
      'Inventory management',
      'Mobile access',
      'Reporting & analytics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-600',
    businessTypes: ['#asset-management', '#maintenance', '#equipment', '#tracking', '#lifecycle']
  },
  {
    id: 'quality-management-system',
    title: 'Quality Management System',
    subtitle: 'ISO compliant quality control',
    description: 'Comprehensive quality management with ISO compliance, document control, audit management, and continuous improvement tracking.',
    icon: Factory,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'ISO compliance management',
      'Document control system',
      'Audit management',
      'Non-conformance tracking',
      'Corrective action plans',
      'Training management',
      'Supplier quality',
      'Statistical process control'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-emerald-500 to-green-600',
    businessTypes: ['#quality', '#iso', '#compliance', '#audit', '#improvement']
  },
  {
    id: 'inventory-optimization-platform',
    title: 'Inventory Optimization Platform',
    subtitle: 'Smart inventory control',
    description: 'AI-powered inventory optimization with demand forecasting, stock level optimization, and automated replenishment.',
    icon: Package,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'AI demand forecasting',
      'Stock optimization',
      'Automated replenishment',
      'ABC analysis',
      'Safety stock calculation',
      'Inventory valuation',
      'Multi-location support',
      'Performance analytics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-cyan-600',
    businessTypes: ['#inventory', '#optimization', '#ai', '#forecasting', '#automation']
  },
  {
    id: 'demand-planning-software',
    title: 'Demand Planning Software',
    subtitle: 'Predictive demand analytics',
    description: 'Advanced demand planning with machine learning forecasting, seasonal analysis, and collaborative planning tools.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Machine learning forecasting',
      'Seasonal analysis',
      'Collaborative planning',
      'Scenario modeling',
      'Exception management',
      'Statistical forecasting',
      'New product forecasting',
      'Performance metrics'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#demand-planning', '#forecasting', '#analytics', '#ml', '#planning']
  },
  {
    id: 'supplier-relationship-management',
    title: 'Supplier Relationship Management',
    subtitle: 'Strategic supplier partnerships',
    description: 'Comprehensive supplier management with performance tracking, risk assessment, and collaboration tools.',
    icon: Cog,
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Supplier onboarding',
      'Performance scorecards',
      'Risk assessment',
      'Contract management',
      'Collaboration portal',
      'Audit management',
      'Payment terms tracking',
      'Certification tracking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#supplier', '#srm', '#risk', '#performance', '#partnerships']
  },
  {
    id: 'procurement-analytics-platform',
    title: 'Procurement Analytics Platform',
    subtitle: 'Data-driven procurement insights',
    description: 'Advanced procurement analytics with spend analysis, supplier performance metrics, and cost optimization insights.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Spend analysis',
      'Supplier performance metrics',
      'Cost optimization',
      'Market intelligence',
      'Contract analytics',
      'Risk monitoring',
      'Savings tracking',
      'Benchmarking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#procurement', '#analytics', '#spend', '#optimization', '#intelligence']
  },
  {
    id: 'logistics-cost-optimization',
    title: 'Logistics Cost Optimisation',
    subtitle: 'Transportation cost management',
    description: 'Optimise logistics costs with route planning, carrier selection, and freight audit capabilities for UK transport networks.',
    icon: Truck,
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Route optimization',
      'Carrier selection',
      'Freight auditing',
      'Cost comparison',
      'Load consolidation',
      'Fuel optimization',
      'Performance tracking',
      'Savings reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-600',
    businessTypes: ['#logistics', '#cost-optimization', '#transportation', '#freight', '#routing']
  },
  {
    id: 'lean-manufacturing-system',
    title: 'Lean Manufacturing System',
    subtitle: 'Operational excellence platform',
    description: 'Lean manufacturing implementation with waste identification, continuous improvement, and operational excellence tools.',
    icon: Factory,
    image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Waste identification',
      'Value stream mapping',
      'Kaizen management',
      '5S implementation',
      'Standard work',
      'Visual management',
      'Performance metrics',
      'Continuous improvement'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-cyan-600',
    businessTypes: ['#lean', '#manufacturing', '#excellence', '#improvement', '#waste-reduction']
  },
  {
    id: 'operations-intelligence-dashboard',
    title: 'Operations Intelligence Dashboard',
    subtitle: 'Real-time operational insights',
    description: 'Comprehensive operations dashboard with real-time KPIs, performance monitoring, and predictive analytics.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Real-time KPIs',
      'Performance monitoring',
      'Predictive analytics',
      'Custom dashboards',
      'Alert management',
      'Trend analysis',
      'Mobile access',
      'Data integration'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#operations', '#dashboard', '#analytics', '#kpi', '#monitoring']
  },
  {
    id: 'performance-benchmarking-tool',
    title: 'Performance Benchmarking Tool',
    subtitle: 'Industry performance comparison',
    description: 'Benchmark operational performance against industry standards with competitive analysis and improvement recommendations.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Industry benchmarking',
      'Competitive analysis',
      'Performance gaps',
      'Improvement recommendations',
      'Best practice library',
      'Maturity assessment',
      'Progress tracking',
      'Reporting tools'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-600',
    businessTypes: ['#benchmarking', '#performance', '#competitive', '#analysis', '#improvement']
  },
  {
    id: 'continuous-improvement-platform',
    title: 'Continuous Improvement Platform',
    subtitle: 'Innovation and improvement management',
    description: 'Systematic continuous improvement with idea management, project tracking, and ROI measurement.',
    icon: Cog,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Operations & Logistics',
    status: 'Available',
    features: [
      'Idea management',
      'Project tracking',
      'ROI measurement',
      'Improvement methodology',
      'Team collaboration',
      'Progress monitoring',
      'Success stories',
      'Knowledge sharing'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#improvement', '#innovation', '#ideas', '#projects', '#collaboration']
  }
];
